import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionsPanel } from './components/ActionsPanel'
import { UserTable } from './components/UserTable'
import './index.less'
import { useAppSelector } from '../../core/hooks'
import {
  useCreateOrUpdatePatientMutation,
  useDeletePatientsMutation,
  useFilterPatientsByDataOwnerQuery,
  useFilterPatientsByFuzzyNameForDataOwnerQuery,
  useGetPatientsByIdsQuery,
} from '../../core/api/patientApi'
import { createSelector } from '@reduxjs/toolkit'
import { CardinalApiState } from '../../core/services/auth.api'
import { AddressType, CodeStub, DecryptedPatient, TelecomType } from '@icure/cardinal-sdk'
import { SelectAllUsersStateType } from './types'
import { AllUsersActionsPanel } from './components/AllUsersActionsPanel'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { message, notification } from 'antd'
import { createPortal } from 'react-dom'
import { ModalConfirmAction } from '../common/ModalConfirmAction'
import { emailsWorkflow } from '../../constants'
import { useSendEmailMutation } from '../../core/api/notificationService'
import { ModalSyncPatientsFromBingli } from './modals/ModalSyncPatientsFromBingli'
import { ModalExportPatients } from './modals/ModalExportPatients'
import { useGetCodesQuery } from '../../core/api/codeApi'
import { useEnsureUserForPatientMutation, useGetPatientIdsWithUserAccountQuery } from '../../core/api/userApi'
import { getPatientDataFormated } from '../../helpers/patientDataManipulations'

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    healthcareParty: cardinalApi.healthcareParty,
  }),
)

export const Patients = () => {
  const { healthcareParty } = useAppSelector(reduxSelector)
  const [searchString, setSearchString] = useState<string | undefined>(undefined)
  const [arePatientsToBeDeleted, setPatientsToBeDeleted] = useState(false)
  const [areEmailsToBeSent, setAreEmailsToBeSent] = useState(false)
  const [arePatientWorkflowsToBeAdvanced, setArePatientWorkflowsToBeAdvanced] = useState(false)
  const [isBingliImportStarted, setIsBingliImportStarted] = useState(false)
  const [isExportSelectedModalOpen, setIsExportSelectedModalOpen] = useState(false)
  const [areUserAccountsToBeCreated, setAreUserAccountsToBeCreated] = useState(false)
  const [ensureUserForPatient] = useEnsureUserForPatientMutation()
  const { data: patientIdsWithUserAccount } = useGetPatientIdsWithUserAccountQuery()
  const patientsWithUserAccountSet = useMemo<ReadonlySet<string>>(() => new Set(patientIdsWithUserAccount ?? []), [patientIdsWithUserAccount])

  const healthcarePartyId = healthcareParty?.id
  const healthcarePartyParentId = healthcareParty?.parentId

  const [deletePatients, { isError: isDeletePatientsError, error: deletePatientsError, isSuccess: arePatientsDeletedSuccessfully, isLoading: isPatientsDeletingLoading }] =
    useDeletePatientsMutation()

  const [sendEmail] = useSendEmailMutation()

  const { data: patientsByDataOwnerIds, isLoading: patientsByDataOwnerIdsAreLoading } = useFilterPatientsByDataOwnerQuery(
    { practitionerId: healthcarePartyId!, parentPractitionerId: healthcarePartyParentId },
    {
      skip: !healthcarePartyId || !!searchString?.length,
    },
  )
  const {
    data: patientsByFuzzyNameForDataOwnerIds,
    isLoading: patientsByFuzzyNameForDataOwnerIdsAreLoading,
    originalArgs: searchArgs,
  } = useFilterPatientsByFuzzyNameForDataOwnerQuery(
    { practitionerId: healthcarePartyId!, parentPractitionerId: healthcarePartyParentId, searchString: searchString?.trim() ?? '' },
    { skip: !healthcarePartyId || !searchString?.length },
  )

  const [createOrUpdatePatient] = useCreateOrUpdatePatientMutation()

  const [api, notificationContextHolder] = notification.useNotification()
  const [messageApi, messageContextHolder] = message.useMessage()

  const openNotification = (type: 'error', message: string, description: string) => {
    api.open({
      type,
      message,
      description,
      duration: 0,
    })
    setTimeout(api.destroy, 5000)
  }

  const showMessageFeedback = (type: 'loading' | 'success' | 'error', content: string) => {
    messageApi.open({
      type,
      content,
      duration: 0,
    })
    // Dismiss manually and asynchronously
    setTimeout(messageApi.destroy, type === 'loading' ? 100 : 2500)
  }

  useEffect(() => {
    if (isPatientsDeletingLoading) showMessageFeedback('loading', 'The patients are deleting...')
    if (arePatientsDeletedSuccessfully) showMessageFeedback('success', 'The patients were deleted!')
    if (isDeletePatientsError)
      openNotification(
        'error',
        'We could not delete the patient!',
        `An error occurred while deleting the patient. ${(deletePatientsError as FetchBaseQueryError)?.data ?? deletePatientsError}`,
      )
  }, [isPatientsDeletingLoading, arePatientsDeletedSuccessfully, isDeletePatientsError, deletePatientsError])

  const patientsIdsList = searchString?.length ? patientsByFuzzyNameForDataOwnerIds : patientsByDataOwnerIds
  const noMatchingPatients = !!searchString?.length && patientsIdsList?.length === 0

  const { data: fullPatientList, isLoading: patientListIsLoading } = useGetPatientsByIdsQuery(patientsIdsList ?? [], {
    skip: !!searchString?.length && searchArgs?.searchString !== searchString,
  })
  const patientsListToRender = !noMatchingPatients ? fullPatientList : []
  const [selectedPatients, setSelectedPatients] = useState<string[]>([])

  useEffect(() => {
    setSelectedPatients((prevSelected) => prevSelected.filter((pId) => fullPatientList?.some((newP) => newP.id === pId)))
  }, [fullPatientList])

  const selectedPatientsState: SelectAllUsersStateType =
    selectedPatients.length === 0 ? 'empty' : selectedPatients.length === fullPatientList?.length ? 'selectedAll' : 'selectedSome'
  const onSelectAllClick = useCallback(() => {
    if (fullPatientList && fullPatientList.length !== 0 && selectedPatients.length !== fullPatientList.length) {
      setSelectedPatients(fullPatientList.map((patient) => patient.id))
    }
  }, [fullPatientList, selectedPatients])
  const onDeselectAllClick = useCallback(() => {
    if (selectedPatients.length > 0) {
      setSelectedPatients([])
    }
  }, [selectedPatients])
  const onPatientSelect = useCallback((selectedPatient: DecryptedPatient, checked: boolean) => {
    setSelectedPatients((prevSelected) => (checked ? [...prevSelected, selectedPatient.id] : prevSelected.filter((pId) => pId !== selectedPatient.id)))
  }, [])

  const selectedPatientObjects = (fullPatientList ?? []).filter((p) => selectedPatients.includes(p.id))
  const selectedPatientsEligibleForUserCreation = selectedPatientObjects.filter((p) => {
    if (patientsWithUserAccountSet.has(p.id)) return false
    const { emailAddress } = getPatientDataFormated(p)
    return !!emailAddress && emailAddress !== '-'
  })

  const runBatchCreateUserAccounts = async () => {
    let created = 0
    let skipped = 0
    let failed = 0
    for (const p of selectedPatientsEligibleForUserCreation) {
      const { emailAddress, userNameOneString } = getPatientDataFormated(p)
      if (!emailAddress || emailAddress === '-' || !userNameOneString) {
        skipped += 1
        continue
      }
      try {
        const res = await ensureUserForPatient({ email: emailAddress, patientId: p.id, name: userNameOneString }).unwrap()
        if (res?.created) created += 1
        else skipped += 1
      } catch (e) {
        console.error('Failed to create user for patient', p.id, e)
        failed += 1
      }
    }
    const ignored = selectedPatientObjects.length - selectedPatientsEligibleForUserCreation.length
    showMessageFeedback('success', `Created ${created} user account${created === 1 ? '' : 's'}, skipped ${skipped + ignored}${failed ? `, failed ${failed}` : ''}.`)
  }

  const patientsSortedByTags = (fullPatientList ?? [])
    .filter((p) => selectedPatients.includes(p.id))
    .reduce(
      (acc, patient) => patient.tags.filter((t) => t.type === 'PREVENTI').reduce((acc, tag) => (tag.code ? { ...acc, [tag.code]: [...(acc[tag.code] ?? []), patient] } : acc), acc),
      {} as Record<string, DecryptedPatient[]>,
    )

  const advanceWorkflowForPatients = (patients: Record<string, DecryptedPatient[]>) => {
    try {
      const transitions = Object.entries(patients).flatMap(([tag, patients]) =>
        patients.flatMap((patient) => {
          const nextTag = emailsWorkflow.find((workflow) => workflow.tag === tag)?.nextTag
          if (!nextTag) {
            throw new Error(`Next tag not found for tag: ${tag}`)
          }
          return [
            {
              patient,
              tag,
              nextTag,
            },
          ]
        }),
      )

      transitions.forEach(async (transition) => {
        const patient = transition.patient
        if (patient) {
          await createOrUpdatePatient(
            new DecryptedPatient({
              ...transition.patient,
              tags: [
                ...patient.tags.filter((t) => t.type !== 'PREVENTI' || t.code !== transition.tag),
                new CodeStub({
                  id: `PREVENTI|${transition.nextTag}|1`,
                  type: 'PREVENTI',
                  code: transition.nextTag,
                  version: '1',
                }),
              ],
            }),
          ).unwrap()
        }
      })
    } catch (error: unknown) {
      openNotification('error', 'An error occurred while advancing workflows', (error as Error).message)
    }
  }

  const sendEmailsToPatients = (patients: Record<string, DecryptedPatient[]>) => {
    try {
      const emailsToBeSent = Object.entries(patients).map(([tag, patients]) => {
        return {
          tag,
          patients: patients.map((patient) => ({
            id: patient.id,
            firstName: patient.firstName,
            lastName: patient.lastName,
            email: patient.addresses.find((address) => address.addressType === AddressType.Home)?.telecoms.find((telecom) => telecom.telecomType === 'Email')?.telecomNumber,
            language: patient.languages.find((language) => ['fr', 'nl'].includes(language.toLowerCase()))?.toUpperCase() ?? 'FR',
          })),
        }
      })

      const patientsWithMissingEmails = emailsToBeSent.flatMap(({ patients }) => patients.filter(({ email }) => !email))

      if (patientsWithMissingEmails.length > 0) {
        openNotification(
          'error',
          'Some emails could not be sent!',
          `The following patients do not have an email address: ${patientsWithMissingEmails.map((patient) => `${patient.firstName} ${patient.lastName}`).join(', ')}`,
        )
        return
      }

      const emails = emailsToBeSent.flatMap(({ tag, patients }) => {
        return patients.map((patient) => {
          const template = emailsWorkflow.find((workflow) => workflow.tag === tag)?.templates.find((t) => t.language === patient.language)
          if (!template) {
            throw new Error(`Email template not found for tag: ${tag} and language ${patient.language}`)
          }

          return {
            templateId: template.templateId,
            recipient: patient.email!,
            variables: {
              firstName: patient.firstName ?? '',
              lastName: patient.lastName ?? '',
              tag,
            },
          }
        })
      })

      const results = emails.reduce(
        async (accp, email) => {
          const acc = await accp
          try {
            const result = await sendEmail(email).unwrap()
            return acc.concat({ recipient: email.recipient, success: !!result })
          } catch (e) {
            return acc.concat({ recipient: email.recipient, success: false })
          }
        },
        Promise.resolve([] as { recipient: string; success: boolean }[]),
      )

      results.then(async (res) => {
        const failedEmails = res.filter(({ success }) => !success)
        if (failedEmails.length > 0) {
          openNotification('error', 'Some emails could not be sent!', `The following emails could not be sent: ${failedEmails.map(({ recipient }) => recipient).join(', ')}`)
        } else {
          showMessageFeedback('success', 'All emails were sent successfully!')
        }
        const transitions = emailsToBeSent.flatMap(({ tag, patients }) =>
          patients.flatMap((patient) =>
            res.some(({ recipient, success }) => success && recipient === patient.email)
              ? [{ patient: (fullPatientList ?? []).find((p) => p.id === patient.id), tag, nextTag: emailsWorkflow.find((t) => t.tag === tag)?.nextTag }]
              : [],
          ),
        )
        for await (const transition of transitions) {
          const patient = transition.patient
          if (patient) {
            await createOrUpdatePatient(
              new DecryptedPatient({
                ...transition.patient,
                tags: [
                  ...patient.tags.filter((t) => t.type !== 'PREVENTI' || t.code !== transition.tag),
                  new CodeStub({
                    id: `PREVENTI|${transition.nextTag}|1`,
                    type: 'PREVENTI',
                    code: transition.nextTag,
                    version: '1',
                  }),
                ],
              }),
            ).unwrap()
          }
        }
      })
    } catch (error: unknown) {
      openNotification('error', 'An error occured while sending emails', (error as Error).message)
    }
  }

  return (
    <>
      {notificationContextHolder}
      {messageContextHolder}
      <div className="patients">
        <h3>Patients</h3>
        <ActionsPanel onSearch={setSearchString} onClear={() => setSearchString(undefined)} patients={fullPatientList ?? []} />
        {patientsListToRender && patientsListToRender?.length !== 0 && (
          <AllUsersActionsPanel
            selectedPatientsState={selectedPatientsState}
            onSelectAllClick={onSelectAllClick}
            onDeselectAllClick={onDeselectAllClick}
            onSendEmailClick={() => setAreEmailsToBeSent(true)}
            onAdvanceWorkflowClick={() => setArePatientWorkflowsToBeAdvanced(true)}
            onExportSelected={() => setIsExportSelectedModalOpen(true)}
            onImportBingli={() => setIsBingliImportStarted(true)}
            onDeleteAllClick={() => setPatientsToBeDeleted(true)}
            onCreateUserAccountsClick={() => setAreUserAccountsToBeCreated(true)}
            canCreateUserAccounts={selectedPatientsEligibleForUserCreation.length > 0}
          />
        )}
        {healthcarePartyId && (
          <UserTable
            healthcareParty={healthcareParty}
            patientList={patientsListToRender}
            noMatchingPatientsPlaceholder={noMatchingPatients}
            selectedPatients={selectedPatients}
            patientsWithUserAccount={patientsWithUserAccountSet}
            showSpinner={patientsByDataOwnerIdsAreLoading || patientListIsLoading || patientsByFuzzyNameForDataOwnerIdsAreLoading || isPatientsDeletingLoading}
            onPatientSelect={onPatientSelect}
          />
        )}
      </div>
      {arePatientsToBeDeleted &&
        createPortal(
          <ModalConfirmAction
            title="Delete patients"
            description="Are you sure you want to delete these patients? Once deleted, their information can't be recovered, so it's a permanent action."
            yesBtnTitle="Delete"
            noBtnTitle="Cancel"
            onYesClick={() => {
              deletePatients((fullPatientList ?? []).filter((p) => selectedPatients.includes(p.id)))
              setPatientsToBeDeleted(false)
            }}
            onNoClick={() => setPatientsToBeDeleted(false)}
            isVisible={arePatientsToBeDeleted}
            mode="danger"
          />,
          document.body,
        )}
      {areEmailsToBeSent &&
        createPortal(
          <ModalConfirmAction
            title="Send emails"
            yesBtnTitle="Send"
            noBtnTitle="Cancel"
            onYesClick={() => {
              sendEmailsToPatients(patientsSortedByTags)
              setAreEmailsToBeSent(false)
            }}
            onNoClick={() => setAreEmailsToBeSent(false)}
            isVisible={areEmailsToBeSent}
            content={
              <div className="sendEmails_confirmAction">
                <p>Emails for patients will be sent as follows:</p>
                <ul>
                  {Object.entries(patientsSortedByTags).map(([tag, patients]) => {
                    const description = emailsWorkflow.find((workflow) => workflow.tag === tag)?.emailDescription
                    return (
                      <li key={tag}>
                        <span>{description}</span> – {patients.length} {patients.length === 1 ? 'patient' : 'patients'}
                      </li>
                    )
                  })}
                </ul>
              </div>
            }
          />,
          document.body,
        )}
      {arePatientWorkflowsToBeAdvanced &&
        createPortal(
          <ModalConfirmAction
            title="Advance workflows"
            yesBtnTitle="Advance"
            noBtnTitle="Cancel"
            onYesClick={() => {
              advanceWorkflowForPatients(patientsSortedByTags)
              setArePatientWorkflowsToBeAdvanced(false)
            }}
            onNoClick={() => setAreEmailsToBeSent(false)}
            isVisible={arePatientWorkflowsToBeAdvanced}
            content={
              <div className="sendEmails_confirmAction">
                <p>The patients workflows are going to be advanced in the following way:</p>
                <ul>
                  {Object.entries(patientsSortedByTags).map(([tag, patients]) => {
                    const nextTag = emailsWorkflow.find((workflow) => workflow.tag === tag)?.nextTag
                    return (
                      <li key={tag}>
                        <span>
                          {tag} → {nextTag}
                        </span>{' '}
                        – {patients.length} {patients.length === 1 ? 'patient' : 'patients'}
                      </li>
                    )
                  })}
                </ul>
              </div>
            }
          />,
          document.body,
        )}
      {isBingliImportStarted &&
        createPortal(
          <ModalSyncPatientsFromBingli isVisible={isBingliImportStarted} selectedPatients={selectedPatientObjects} onClose={() => setIsBingliImportStarted(false)} />,
          document.body,
        )}
      {isExportSelectedModalOpen &&
        createPortal(
          <ModalExportPatients
            isVisible={isExportSelectedModalOpen}
            onClose={() => setIsExportSelectedModalOpen(false)}
            patients={(fullPatientList ?? []).filter((p) => selectedPatients.includes(p.id))}
          />,
          document.body,
        )}
      {areUserAccountsToBeCreated &&
        createPortal(
          <ModalConfirmAction
            title="Create user accounts"
            yesBtnTitle="Create"
            noBtnTitle="Cancel"
            isVisible={areUserAccountsToBeCreated}
            onYesClick={async () => {
              setAreUserAccountsToBeCreated(false)
              await runBatchCreateUserAccounts()
            }}
            onNoClick={() => setAreUserAccountsToBeCreated(false)}
            content={
              <div className="sendEmails_confirmAction">
                <p>
                  About to create user accounts for {selectedPatientsEligibleForUserCreation.length} of {selectedPatientObjects.length} selected patient
                  {selectedPatientObjects.length === 1 ? '' : 's'}. Patients without an email or already linked to a user account will be skipped.
                </p>
                <p>An invitation email will be sent to each new account.</p>
              </div>
            }
          />,
          document.body,
        )}
    </>
  )
}
